import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Lock, ArrowRight } from "lucide-react";

export default function AdminLogin() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, admin } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Already logged in → go to dashboard (effect, not during render)
    useEffect(() => {
        if (admin) navigate("/admin", { replace: true });
    }, [admin, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Accesso effettuato");
            const to = location.state?.from || "/admin";
            navigate(to, { replace: true });
        } catch (err) {
            toast.error(formatApiError(err, "Credenziali non valide"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main
            data-testid="admin-login-page"
            className="min-h-screen bg-ink-900 flex items-center justify-center px-6 pt-24 pb-12"
        >
            <div className="w-full max-w-md">
                <Link
                    to="/"
                    data-testid="admin-login-home-link"
                    className="block mb-12"
                >
                    <img
                        src="/logo-dogaparquet.png"
                        alt="Dogaparquet"
                        className="h-14 w-auto mx-auto"
                    />
                </Link>

                <div className="bg-ink-800 border border-white/10 p-10">
                    <div className="flex items-center gap-3 mb-2">
                        <Lock size={16} className="text-brass" />
                        <p className="text-xs uppercase tracking-widest text-brass">
                            Area riservata
                        </p>
                    </div>
                    <h1 className="font-serif text-3xl text-cream font-light leading-tight">
                        Accedi al pannello.
                    </h1>
                    <p className="mt-3 text-sm text-cream/60">
                        Inserisci le credenziali di amministratore per gestire portfolio e messaggi.
                    </p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
                        <div>
                            <label
                                htmlFor="login-email"
                                className="block text-[10px] uppercase tracking-widest text-brass mb-2"
                            >
                                Email
                            </label>
                            <input
                                id="login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@dogaparquet.com"
                                data-testid="admin-login-email"
                                className="input-minimal"
                                autoComplete="email"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="login-password"
                                className="block text-[10px] uppercase tracking-widest text-brass mb-2"
                            >
                                Password
                            </label>
                            <input
                                id="login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••••"
                                data-testid="admin-login-password"
                                className="input-minimal"
                                autoComplete="current-password"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="admin-login-submit"
                            className="group w-full mt-4 inline-flex items-center justify-center gap-3 bg-brass hover:bg-brass-light disabled:opacity-60 text-ink-900 px-8 py-4 text-sm tracking-widest uppercase transition-colors duration-500"
                        >
                            {submitting ? "Accesso…" : "Accedi"}
                            <ArrowRight
                                size={16}
                                className="transition-transform duration-500 group-hover:translate-x-1"
                            />
                        </button>
                    </form>
                </div>

                <Link
                    to="/"
                    className="block mt-8 text-center text-xs uppercase tracking-widest text-cream/40 hover:text-brass transition-colors"
                    data-testid="admin-login-back"
                >
                    ← Torna al sito
                </Link>
            </div>
        </main>
    );
}
